/**
 * Who answers Perzo support and how fast (owner's decision, 2026-10-16). Merchant-facing, in English.
 * Every reply from the admin inbox is signed SUPPORT_SIGNATURE, whoever on the team typed it.
 */
export const SUPPORT_PERSON = 'Olavo';
export const SUPPORT_SIGNATURE = 'Olavo · Perzo';
export const SUPPORT_INTRO = "You talk to Olavo, Perzo's founder.";
export const SUPPORT_PROMISE = 'Replies within 1 business day (US hours).';
export const SUPPORT_EMAIL = 'support@perzo.art';

/**
 * Support reply promise: "within 1 business day (US hours)". A store message waits on business time
 * only: Saturdays and Sundays in US Eastern time do not count. States, by business hours waited:
 * ok < 12 h, warning 12–24 h, overdue > 24 h (one business day). Pure: used by the admin inbox (web)
 * and the nav badge count (server).
 */

export type SlaState = 'ok' | 'warning' | 'overdue';

export const SLA_WARNING_HOURS = 12;
export const SLA_OVERDUE_HOURS = 24;
export const SLA_TIME_ZONE = 'America/New_York';

const HOUR = 3_600_000;
const DAY = 24 * HOUR;
/** Past this much wall-clock time the walk stops: the state is overdue whatever the calendar. */
const MAX_WALK = 60 * DAY;

let parts: Intl.DateTimeFormat | null = null;
function eastern(t: number): { weekend: boolean; sinceMidnight: number } {
  parts ??= new Intl.DateTimeFormat('en-US', { timeZone: SLA_TIME_ZONE, weekday: 'short', hour: 'numeric', minute: 'numeric', second: 'numeric', hourCycle: 'h23' });
  const p = Object.fromEntries(parts.formatToParts(new Date(t)).map((x) => [x.type, x.value]));
  const sinceMidnight = (Number(p.hour) % 24) * HOUR + Number(p.minute) * 60_000 + Number(p.second) * 1000 + (t % 1000 + 1000) % 1000;
  return { weekend: p.weekday === 'Sat' || p.weekday === 'Sun', sinceMidnight };
}

/** Milliseconds between `from` and `to` that fall on US Eastern weekdays. */
export function businessMsBetween(from: Date | string | number, to: Date | string | number): number {
  let t = new Date(from).getTime();
  const end = new Date(to).getTime();
  if (!Number.isFinite(t) || !Number.isFinite(end) || end <= t) return 0;
  if (end - t > MAX_WALK) t = end - MAX_WALK;
  let total = 0;
  while (t < end) {
    const here = eastern(t);
    // The next Eastern midnight. On a daylight-saving day the 24 h step lands an hour off: an hour past
    // midnight is pulled back to it; an hour before midnight just takes one more (short) step.
    let next = t + (DAY - here.sinceMidnight);
    const landed = eastern(next).sinceMidnight;
    if (landed > 0 && landed < 3 * HOUR) next -= landed;
    if (next <= t) next = t + HOUR;
    const stop = Math.min(next, end);
    if (!here.weekend) total += stop - t;
    t = stop;
  }
  return total;
}

/** The state of a message waiting since `since` (null: nothing waiting). */
export function slaState(since: Date | string | number | null | undefined, now: Date | string | number): SlaState | null {
  if (since === null || since === undefined) return null;
  const hours = businessMsBetween(since, now) / HOUR;
  if (hours > SLA_OVERDUE_HOURS) return 'overdue';
  if (hours >= SLA_WARNING_HOURS) return 'warning';
  return 'ok';
}

/**
 * Admin sound alert: whether the newest store message is new since the baseline. `undefined` baseline:
 * nothing seen yet (first load): never an alert, the current value becomes the baseline.
 */
export function detectNewMessage(baseline: string | null | undefined, latest: string | null | undefined): { alert: boolean; baseline: string | null } {
  const now = latest ?? null;
  if (baseline === undefined) return { alert: false, baseline: now };
  if (!now) return { alert: false, baseline };
  if (!baseline) return { alert: true, baseline: now };
  const a = Date.parse(now);
  const b = Date.parse(baseline);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return { alert: false, baseline };
  return a > b ? { alert: true, baseline: now } : { alert: false, baseline };
}

const RANK: Record<SlaState, number> = { overdue: 0, warning: 1, ok: 2 };

/**
 * Overdue first, then warning, then the rest; within overdue and warning the longest wait first.
 * Everything else keeps its order (stable).
 */
export function sortBySla<T>(items: T[], since: (item: T) => string | null | undefined, now: Date | string | number): T[] {
  const keyed = items.map((item, i) => {
    const s = since(item);
    const state = slaState(s, now);
    return { item, i, rank: state ? RANK[state] : 3, at: s && state !== 'ok' ? new Date(s).getTime() : 0 };
  });
  keyed.sort((a, b) => a.rank - b.rank || a.at - b.at || a.i - b.i);
  return keyed.map((k) => k.item);
}
